import { useAuth } from './useAuth';
import { Role } from '../types';

export type Feature =
  | 'dashboard_view'
  | 'apartments_view'
  | 'apartments_manage'
  | 'events_view'
  | 'members_view'
  | 'members_manage'
  | 'stats_view'
  | 'hard_delete'
  | 'announcements_manage'
  | 'companies_manage'
  | 'audit_log_view'
  | 'alert_bell'
  | 'realtime_status_view';

const MATRIX: Record<Feature, Role[]> = {
  dashboard_view: ['super_admin', 'admin', 'manager'],
  apartments_view: ['super_admin', 'admin', 'manager'],
  apartments_manage: ['super_admin', 'admin'],
  events_view: ['super_admin', 'admin', 'manager'],
  members_view: ['super_admin', 'admin', 'manager'],
  members_manage: ['super_admin', 'admin'],
  stats_view: ['super_admin', 'admin', 'manager'],
  hard_delete: ['super_admin'],
  announcements_manage: ['super_admin'],
  companies_manage: ['super_admin', 'admin'],
  // ISS-035 — 감사 로그는 super_admin 전용. 이전 audit_log_view_all / audit_log_view_scoped 두 키를 단일 키로 통합
  audit_log_view: ['super_admin'],
  alert_bell: ['super_admin', 'admin', 'manager'],
  // ISS-043 — 대시보드 "실시간 연결" Card 노출 (시각 위젯만 한정, SSE 연결/Bell/피드는 staff 전체 보존)
  realtime_status_view: ['super_admin', 'admin'],
};

export const usePermission = () => {
  const { role } = useAuth();

  const hasRole = (...roles: Role[]) => role !== null && roles.includes(role as Role);

  const isSuperAdmin = role === 'super_admin';
  const isAdmin = role === 'admin';
  const isManager = role === 'manager';
  const isUser = role === 'user';
  const isStaff = isSuperAdmin || isAdmin || isManager;

  const canAccess = (feature: Feature): boolean => {
    if (!role) return false;
    return MATRIX[feature].includes(role as Role);
  };

  const canPromoteTo = (target: Role): boolean => {
    if (!role) return false;
    if (isSuperAdmin) return target === 'super_admin' || target === 'admin' || target === 'manager';
    if (isAdmin) return target === 'manager';
    return false;
  };

  return {
    hasRole,
    canAccess,
    canPromoteTo,
    role,
    isSuperAdmin,
    isAdmin,
    isManager,
    isUser,
    isStaff,
  };
};
